// frontend/src/pages/ConfirmSubscription.tsx
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { newsletterAPI } from '../services/api';
import './ConfirmSubscription.css';

const ConfirmSubscription: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const confirmSubscription = async () => {
      if (!token) {
        setError('Invalid confirmation link');
        setLoading(false);
        return;
      }

      try {
        const response = await newsletterAPI.confirmSubscription(token);
        setSuccess(true);
        // Redirect to home after 5 seconds
        setTimeout(() => {
          navigate('/');
        }, 5000);
      } catch (err: any) {
        console.error('Confirmation error:', err);
        const errorMsg = err.response?.data?.error || 'Failed to confirm subscription';
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    confirmSubscription();
  }, [token, navigate]);

  // SVG Icons
  const CheckIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );

  const CloseIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );

  const SpinnerIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );

  if (loading) {
    return (
      <div className="confirm-banner">
        <div className="confirm-container">
          <div className="confirm-card">
            <div className="loading-spinner">
              <SpinnerIcon />
            </div>
            <h2>Confirming your subscription...</h2>
            <p>Please wait while we verify your email.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="confirm-banner">
        <div className="confirm-container">
          <div className="confirm-card error">
            <div className="icon-circle error-icon">
              <CloseIcon />
            </div>
            <h2>Confirmation Failed</h2>
            <p>{error}</p>
            <Link to="/" className="confirm-btn confirm-btn-error">Go to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="confirm-banner">
      <div className="confirm-container">
        <div className="confirm-card success">
          <div className="icon-circle success-icon">
            <CheckIcon />
          </div>
          <h2>Subscription Confirmed!</h2>
          <p>Thank you for confirming your email address. You are now subscribed to our newsletter.</p>
          <p className="redirect-message">You will be redirected to the home page in a few seconds...</p>
          <Link to="/" className="confirm-btn confirm-btn-success">Go to Home Now</Link>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSubscription;